import React, { useEffect, useRef, useState } from "react";
import { AccountCircle, DarkMode, HelpOutline, LightMode, Logout, Menu, Notifications, Search, Settings } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import {
  fetchNotifications,
  fetchUnreadNotificationsCount,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../services/owdedApi";
import { BASE_URL } from "../../services/constant";

function Header({ onToggleSidebar, sidebarOpen = true }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const menuRef = useRef(null);
  const notificationsRef = useRef(null);

  const avatarUrl = user?.photo_url
    ? (user.photo_url.startsWith("http") ? user.photo_url : `${BASE_URL}${user.photo_url}`)
    : "";

  const loadNotifications = async () => {
    try {
      const [items, unread] = await Promise.all([
        fetchNotifications({ limit: 12 }),
        fetchUnreadNotificationsCount(),
      ]);
      setNotifications(items || []);
      setUnreadCount(unread?.total || 0);
    } catch (err) {
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    loadNotifications();
    const timer = setInterval(loadNotifications, 60000);
    return () => clearInterval(timer);
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const openNotification = async (notification) => {
    if (!notification.read_at) {
      await markNotificationRead(notification.id);
    }
    await loadNotifications();
    if (notification.action_url) {
      navigate(notification.action_url);
      setNotificationsOpen(false);
    }
  };

  const readAll = async () => {
    await markAllNotificationsRead();
    await loadNotifications();
  };

  const pageMeta = {
    "/dashboard": ["Tableau de bord", "Vue d'ensemble du portefeuille"],
    "/clients": ["Gestion des clients", "Fiches, activites et historique"],
    "/products": ["Produits", "Sunga Biz, Bootstrapping et plafonds"],
    "/investments": ["Investissements", "Demandes, validation et recouvrement"],
    "/recoveries": ["Recouvrements", "Encaissements et rapports agents"],
    "/treasury": ["Tresorerie", "Caisse, banque, entrees et sorties"],
    "/funds": ["Fonds", "Disponibilites et capitaux investis"],
    "/reports": ["Rapports", "Performances et etats PDF"],
    "/audit": ["Controle interne", "Audit, tracabilite et supervision"],
    "/users": ["Utilisateurs", "Agents, fonctions et acces"],
    "/mobile": ["Espace mobile", "Saisie terrain et recouvrement"],
  };
  const [pageTitle, pageSubtitle] = pageMeta[location.pathname] || ["OwSur Invest", "Gestion investissement et recouvrement"];

  return (
    <header className="header">
      <div className="header-main">
        <button
          className="icon-btn menu-trigger"
          type="button"
          aria-label={sidebarOpen ? "Masquer le menu" : "Afficher le menu"}
          aria-pressed={sidebarOpen}
          onClick={onToggleSidebar}
        >
          <Menu />
        </button>
        <div className="header-title">
          <h1>{pageTitle}</h1>
          <small>{pageSubtitle}</small>
        </div>
      </div>

      <div className="search-bar">
        <input type="text" placeholder="Rechercher (Ctrl + K)" />
        <button type="button" aria-label="Rechercher">
          <Search />
        </button>
      </div>

      <div className="icons">
        <button className="icon-btn" type="button" onClick={toggleTheme} aria-label="Changer le theme">
          {theme === "dark" ? <LightMode /> : <DarkMode />}
        </button>
        <button className="icon-btn" type="button" aria-label="Aide">
          <HelpOutline />
        </button>
        <div className="notification-box" ref={notificationsRef}>
          <button
            className="icon-btn notification-trigger"
            type="button"
            onClick={() => {
              setNotificationsOpen((value) => !value);
              loadNotifications();
            }}
            aria-label="Notifications"
          >
            <Notifications />
            {unreadCount > 0 && <span className="notification-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>}
          </button>

          {notificationsOpen && (
            <div className="notifications-menu">
              <div className="notifications-menu__head">
                <strong>Notifications</strong>
                <button type="button" onClick={readAll}>Tout lire</button>
              </div>
              <div className="notifications-menu__list">
                {notifications.length ? notifications.map((notification) => (
                  <button
                    key={notification.id}
                    className={`notification-item ${notification.read_at ? "" : "is-unread"}`}
                    type="button"
                    onClick={() => openNotification(notification)}
                  >
                    <span className={`notification-dot priority-${String(notification.priority || "NORMAL").toLowerCase()}`} />
                    <span>
                      <strong>{notification.title}</strong>
                      <small>{notification.message}</small>
                    </span>
                  </button>
                )) : (
                  <div className="notification-empty">Aucune notification</div>
                )}
              </div>
            </div>
          )}
        </div>
        <span className="user-chip">
          <strong>{user?.full_name || user?.email}</strong>
          <small>{user?.role_label || user?.role_code}</small>
        </span>
        <div className="account" ref={menuRef}>
          <button className="account-avatar" type="button" onClick={() => setOpen((value) => !value)} aria-label="Compte utilisateur">
            {avatarUrl ? <img src={avatarUrl} alt={user?.full_name || "Utilisateur"} /> : <AccountCircle className="account-icon" />}
          </button>
          {open && (
            <div className="account-menu">
              <button type="button" onClick={() => navigate("/users")}>
                <Settings />
                <span>Parametres</span>
              </button>
              <hr />
              <button type="button" className="logout" onClick={handleLogout}>
                <Logout />
                <span>Deconnexion</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
